import { useEffect, useMemo, useState } from 'react';
import { api, resolveApiMediaUrl } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';
import { formatBrl } from '../utils/currency.js';
import { Link } from 'react-router-dom';

const emptyCreateForm = () => ({
  nome: '',
  preco: '',
  secao: 'Geral',
  grupo: 'Geral',
  descricao: '',
  imagem: '',
  imagem_2: '',
  imagem_3: '',
  imagem_4: '',
  youtube_video_url: '',
  destaque: false,
});

/** @param {unknown} v */
function formatEstoqueCell(v) {
  if (v === null || v === undefined || v === '') {
    return { text: '—', title: 'Sem quantidade registada (NULL no sistema — assim o ERP/sync ou você podem definir depois).' };
  }
  const n = typeof v === 'number' ? v : Number(String(v));
  if (!Number.isFinite(n)) {
    return { text: String(v), title: 'Quantidade em estoque' };
  }
  return {
    text: new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(Math.trunc(n)),
    title: 'Quantidade em estoque',
  };
}

function productToEditForm(p) {
  const estRaw = p.estoque;
  return {
    id: p.id,
    nome: p.nome ?? '',
    preco: p.preco != null ? String(p.preco) : '',
    estoque: estRaw != null && estRaw !== '' ? String(estRaw) : '',
    secao: p.secao ?? 'Geral',
    grupo: p.grupo ?? 'Geral',
    descricao: p.descricao ?? '',
    imagem: p.imagem ?? '',
    imagem_2: p.imagem_2 ?? '',
    imagem_3: p.imagem_3 ?? '',
    imagem_4: p.imagem_4 ?? '',
    youtube_video_url: p.youtube_video_url ?? '',
    destaque: Boolean(Number(p.destaque)),
  };
}

function IconPencil() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function ProductsPage() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyCreateForm());
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editPreviewFailed, setEditPreviewFailed] = useState(false);
  const [createPhotoFile, setCreatePhotoFile] = useState(null);
  /** Ficheiros para slots 2–4 (principal continua createPhotoFile + URL). */
  const [createPhotoExtra, setCreatePhotoExtra] = useState([null, null, null]);
  const [createPhotoKey, setCreatePhotoKey] = useState(0);
  const [createPhotoExtraKey, setCreatePhotoExtraKey] = useState(0);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoExtra, setEditPhotoExtra] = useState([null, null, null]);
  const [error, setError] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [sectionNames, setSectionNames] = useState([]);

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.nome ?? '').toLowerCase().includes(q));
  }, [items, nameFilter]);

  /** @param slot 1–4 (1 = foto principal) */
  async function uploadProductImage(tenantId, productId, file, slot = 1) {
    const fd = new FormData();
    fd.append('imagem', file);
    const q = slot >= 2 ? `?slot=${slot}` : '';
    await api(`/tenants/${tenantId}/products/${productId}/imagem${q}`, {
      method: 'POST',
      body: fd,
    });
  }

  useEffect(() => {
    setEditPreviewFailed(false);
  }, [editing?.id, editing?.imagem]);

  async function load() {
    if (!tenantId) return;
    const data = await api(`/tenants/${tenantId}/products`);
    setItems(data.products || []);
  }

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    api(`/tenants/${tenantId}/sections`)
      .then((d) => {
        const names = (d.sections || [])
          .map((s) => String(s?.nome ?? '').trim())
          .filter((n) => n !== '');
        setSectionNames(names);
      })
      .catch(() => setSectionNames([]));
  }, [tenantId]);

  async function add(e) {
    e.preventDefault();
    setError('');
    try {
      const img = form.imagem.trim();
      const i2 = form.imagem_2.trim();
      const i3 = form.imagem_3.trim();
      const i4 = form.imagem_4.trim();
      const yt = form.youtube_video_url.trim();
      const data = await api(`/tenants/${tenantId}/products`, {
        method: 'POST',
        body: JSON.stringify({
          nome: form.nome,
          preco: Number(form.preco),
          secao: form.secao,
          grupo: form.grupo,
          descricao: form.descricao || null,
          imagem: createPhotoFile ? null : img || null,
          imagem_2: createPhotoExtra[0] ? null : i2 || null,
          imagem_3: createPhotoExtra[1] ? null : i3 || null,
          imagem_4: createPhotoExtra[2] ? null : i4 || null,
          youtube_video_url: yt || null,
          destaque: Boolean(form.destaque),
        }),
      });
      const pid = data?.product?.id;
      if (pid) {
        if (createPhotoFile) await uploadProductImage(tenantId, pid, createPhotoFile, 1);
        if (createPhotoExtra[0]) await uploadProductImage(tenantId, pid, createPhotoExtra[0], 2);
        if (createPhotoExtra[1]) await uploadProductImage(tenantId, pid, createPhotoExtra[1], 3);
        if (createPhotoExtra[2]) await uploadProductImage(tenantId, pid, createPhotoExtra[2], 4);
      }
      setCreatePhotoFile(null);
      setCreatePhotoExtra([null, null, null]);
      setCreatePhotoKey((k) => k + 1);
      setCreatePhotoExtraKey((k) => k + 1);
      setForm(emptyCreateForm());
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editing || !tenantId) return;
    setError('');
    try {
      const img = editing.imagem.trim();
      const estTrim = editing.estoque.trim();
      let estoqueBody = /** @type{number|null} */ (null);
      if (estTrim !== '') {
        const nEst = Math.trunc(Number(estTrim.replace(',', '.')));
        if (!Number.isFinite(nEst) || nEst < 0) {
          setError('Quantidade em estoque inválida (use um número inteiro ≥ 0 ou deixe vazio para sem controlo).');
          return;
        }
        estoqueBody = nEst;
      }
      const body = {
        nome: editing.nome,
        preco: Number(editing.preco),
        estoque: estoqueBody,
        secao: editing.secao,
        grupo: editing.grupo,
        descricao: editing.descricao || null,
        imagem_2: editPhotoExtra[0] ? undefined : editing.imagem_2.trim() || null,
        imagem_3: editPhotoExtra[1] ? undefined : editing.imagem_3.trim() || null,
        imagem_4: editPhotoExtra[2] ? undefined : editing.imagem_4.trim() || null,
        youtube_video_url: editing.youtube_video_url.trim() || null,
        destaque: Boolean(editing.destaque),
      };
      if (!editPhotoFile) {
        body.imagem = img || null;
      }
      await api(`/tenants/${tenantId}/products/${editing.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const eid = editing.id;
      if (editPhotoFile) await uploadProductImage(tenantId, eid, editPhotoFile, 1);
      if (editPhotoExtra[0]) await uploadProductImage(tenantId, eid, editPhotoExtra[0], 2);
      if (editPhotoExtra[1]) await uploadProductImage(tenantId, eid, editPhotoExtra[1], 3);
      if (editPhotoExtra[2]) await uploadProductImage(tenantId, eid, editPhotoExtra[2], 4);
      setEditPhotoFile(null);
      setEditPhotoExtra([null, null, null]);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !tenantId) return;
    setError('');
    try {
      await api(`/tenants/${tenantId}/products/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err.message);
      setDeleteTarget(null);
    }
  }

  if (!tenantId) {
    return (
      <div className="py-12 text-center text-sm text-ink/60 dark:text-frost-muted">
        Selecione uma empresa em{' '}
        <Link className="font-semibold text-accent" to="/app/empresa">
          Empresas
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-semibold">Produtos</h2>
          <p className="text-sm text-ink/60 dark:text-frost-muted">Catálogo manual do tenant selecionado.</p>
        </div>
        <Link
          to="/app/secoes"
          className="text-sm font-semibold text-accent underline-offset-4 hover:underline"
        >
          Secções e fotos →
        </Link>
      </div>

      <form
        onSubmit={add}
        className="grid gap-3 rounded-3xl border border-ink/10 bg-white/80 p-6 shadow-lg backdrop-blur dark:border-white/10 dark:bg-white/5 md:grid-cols-2"
      >
        <label className="text-sm font-semibold md:col-span-2">
          Nome
          <input
            required
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
          />
        </label>
        <label className="text-sm font-semibold">
          Preço
          <input
            required
            type="number"
            min="0"
            step="0.01"
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.preco}
            onChange={(e) => setForm({ ...form, preco: e.target.value })}
          />
        </label>
        <label className="text-sm font-semibold md:col-span-2">
          URL da foto (opcional se enviar ficheiro)
          <input
            type="text"
            inputMode="url"
            placeholder="https://…"
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.imagem}
            onChange={(e) => setForm({ ...form, imagem: e.target.value })}
          />
        </label>
        <label className="text-sm font-semibold md:col-span-2">
          Ou ficheiro (JPEG, PNG, WebP ou GIF, até ~2&nbsp;MiB)
          <input
            key={createPhotoKey}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
            className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
            onChange={(e) => setCreatePhotoFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <div className="md:col-span-2 space-y-3 rounded-2xl border border-ink/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <p className="text-xs font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
            Galeria (opcional) — fotos 2 a 4 e vídeo
          </p>
          <p className="text-[11px] text-ink/50 dark:text-frost-muted">
            URL ou caminho (como a foto principal), ou escolha ficheiro — o ficheiro prevalece sobre a URL ao guardar.
          </p>
          {[2, 3, 4].map((slot, idx) => (
            <div key={slot} className="grid gap-2 sm:grid-cols-2 sm:items-end">
              <label className="text-sm font-semibold sm:col-span-1">
                URL foto {slot}
                <input
                  type="text"
                  inputMode="url"
                  placeholder="https://… ou /api/uploads/…"
                  disabled={!!createPhotoExtra[idx]}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-ink"
                  value={slot === 2 ? form.imagem_2 : slot === 3 ? form.imagem_3 : form.imagem_4}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ...(slot === 2 ? { imagem_2: e.target.value } : slot === 3 ? { imagem_3: e.target.value } : { imagem_4: e.target.value }),
                    })
                  }
                />
              </label>
              <label className="text-sm font-semibold sm:col-span-1">
                Ficheiro foto {slot}
                <input
                  key={`${createPhotoExtraKey}-x-${slot}`}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setCreatePhotoExtra((prev) => {
                      const n = [...prev];
                      n[idx] = f;
                      return n;
                    });
                  }}
                />
              </label>
            </div>
          ))}
          <label className="block text-sm font-semibold">
            Link do YouTube (opcional)
            <input
              type="text"
              inputMode="url"
              autoComplete="off"
              placeholder="https://www.youtube.com/watch?v=…"
              className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
              value={form.youtube_video_url}
              onChange={(e) => setForm({ ...form, youtube_video_url: e.target.value })}
            />
          </label>
        </div>
        <label className="text-sm font-semibold">
          Seção
          <input
            list="produto-secao-sugestoes"
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.secao}
            onChange={(e) => setForm({ ...form, secao: e.target.value })}
          />
        </label>
        <label className="text-sm font-semibold">
          Grupo
          <input
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.grupo}
            onChange={(e) => setForm({ ...form, grupo: e.target.value })}
          />
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold md:col-span-2">
          <input
            type="checkbox"
            checked={Boolean(form.destaque)}
            onChange={(e) => setForm({ ...form, destaque: e.target.checked })}
            className="h-4 w-4 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
          />
          Em destaque na vitrine (secção dinâmica e loja)
        </label>
        <label className="text-sm font-semibold md:col-span-2">
          Descrição
          <textarea
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            rows={2}
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
          />
        </label>
        {error && !editing && <p className="md:col-span-2 text-sm font-semibold text-accent2">{error}</p>}
        <button
          type="submit"
          className="md:col-span-2 rounded-2xl bg-accent py-3 text-sm font-semibold text-white"
        >
          Adicionar produto
        </button>
      </form>

      <div className="overflow-x-auto rounded-3xl border border-ink/10 bg-white/80 shadow-lg dark:border-white/10 dark:bg-white/5">
        <div className="border-b border-ink/10 p-4 dark:border-white/5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <label className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
              Filtrar por nome
              <input
                type="search"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Digite o nome — a lista atualiza ao digitar"
                autoComplete="off"
                spellCheck="false"
                className="mt-1.5 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-ink placeholder:text-ink/35 dark:border-white/10 dark:bg-ink dark:text-mist dark:placeholder:text-mist/40"
              />
            </label>
            {nameFilter.trim() !== '' && (
              <button
                type="button"
                className="shrink-0 rounded-xl border border-ink/15 px-3 py-2 text-xs font-semibold text-ink/70 hover:bg-black/5 dark:border-white/15 dark:text-mist/80 dark:hover:bg-white/10"
                onClick={() => setNameFilter('')}
              >
                Limpar filtro
              </button>
            )}
          </div>
          {items.length > 0 && (
            <p className="mt-2 text-xs text-ink/50 dark:text-frost-muted">
              {filteredItems.length === items.length
                ? `${items.length} produto${items.length === 1 ? '' : 's'}`
                : `Mostrando ${filteredItems.length} de ${items.length} produto${items.length === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="bg-black/5 text-xs font-semibold uppercase tracking-wide dark:bg-white/5">
            <tr>
              <th className="px-4 py-3">Foto</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3 whitespace-nowrap">Destaque</th>
              <th className="px-4 py-3">Preço</th>
              <th className="px-4 py-3 whitespace-nowrap">Estoque</th>
              <th className="px-4 py-3">Seção / grupo</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="border-t border-ink/5 px-4 py-10 text-center text-sm text-ink/55 dark:border-white/5 dark:text-frost-muted">
                  {items.length === 0
                    ? 'Nenhum produto cadastrado.'
                    : 'Nenhum produto corresponde ao filtro. Ajuste o termo ou limpe a pesquisa.'}
                </td>
              </tr>
            ) : (
              filteredItems.map((p) => {
                const st = formatEstoqueCell(p.estoque);
                return (
              <tr key={p.id} className="border-t border-ink/5 dark:border-white/5">
                <td className="px-4 py-2">
                  {p.imagem ? (
                    <img
                      src={resolveApiMediaUrl(p.imagem)}
                      alt=""
                      className="h-11 w-11 rounded-xl border border-ink/10 object-cover dark:border-white/10"
                      loading="lazy"
                      onError={(ev) => {
                        ev.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-black/5 text-[10px] text-ink/40 dark:bg-white/5 dark:text-mist/40">
                      —
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-semibold">{p.nome}</td>
                <td className="px-4 py-3 text-ink/70 dark:text-mist/70">
                  {p.destaque === true || Number(p.destaque) === 1 ? 'Sim' : '—'}
                </td>
                <td className="px-4 py-3">{formatBrl(p.preco)}</td>
                <td
                  className="px-4 py-3 whitespace-nowrap font-mono text-sm tabular-nums text-ink/85 dark:text-mist/85"
                  title={st.title}
                >
                  {st.text}
                </td>
                <td className="px-4 py-3 text-ink/60 dark:text-frost-muted">
                  {p.secao} · {p.grupo}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      aria-label="Editar produto"
                      title="Editar"
                      className="rounded-xl p-2 text-accent hover:bg-accent/10 dark:hover:bg-accent/20"
                      onClick={() => {
                        setError('');
                        setEditPreviewFailed(false);
                        setEditPhotoFile(null);
                        setEditPhotoExtra([null, null, null]);
                        setEditing(productToEditForm(p));
                      }}
                    >
                      <IconPencil />
                    </button>
                    <button
                      type="button"
                      aria-label="Excluir produto"
                      title="Excluir"
                      className="rounded-xl p-2 text-accent2 hover:bg-accent2/10"
                      onClick={() => {
                        setError('');
                        setDeleteTarget({ id: p.id, nome: p.nome });
                      }}
                    >
                      <IconTrash />
                    </button>
                  </div>
                </td>
              </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => {
            setEditing(null);
            setError('');
          }}
        >
          <div
            role="dialog"
            aria-labelledby="edit-product-title"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#161f1c]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="edit-product-title" className="font-display text-xl font-semibold">
              Editar produto
            </h3>
            <p className="mt-1 text-xs text-ink/50 dark:text-frost-muted">
              Foto principal: URL ou novo ficheiro (o ficheiro prevalece). Pode acrescentar até mais três fotos e um link de vídeo YouTube.
            </p>
            <form className="mt-4 grid gap-3" onSubmit={saveEdit}>
              <label className="text-sm font-semibold">
                Nome
                <input
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.nome}
                  onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
                />
              </label>
              <label className="text-sm font-semibold">
                Preço
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.preco}
                  onChange={(e) => setEditing({ ...editing, preco: e.target.value })}
                />
              </label>
              <label className="text-sm font-semibold">
                Quantidade em estoque
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="vazio = sem controlo na base"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.estoque}
                  onChange={(e) => setEditing({ ...editing, estoque: e.target.value })}
                />
              </label>
              <p className="-mt-2 text-[11px] text-ink/50 dark:text-frost-muted">
                Número inteiro ≥ 0 ou deixe vazio para gravar sem quantidade definida na base (NULL).
              </p>
              <label className="text-sm font-semibold">
                URL da foto
                <input
                  type="text"
                  inputMode="url"
                  placeholder="https://…"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.imagem}
                  onChange={(e) => setEditing({ ...editing, imagem: e.target.value })}
                />
              </label>
              <label className="text-sm font-semibold">
                Novo ficheiro da foto (substitui a guardada ao guardar)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
                  onChange={(e) => setEditPhotoFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {!editPhotoFile && editing.imagem.trim() !== '' ? (
                <div className="flex items-center gap-3 rounded-xl border border-ink/10 p-3 dark:border-white/10">
                  {editPreviewFailed ? (
                    <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-black/5 text-center text-[10px] leading-tight text-accent2 dark:bg-white/5">
                      ?
                    </div>
                  ) : (
                    <img
                      src={resolveApiMediaUrl(editing.imagem.trim())}
                      alt=""
                      className="h-16 w-16 rounded-lg bg-black/5 object-cover dark:bg-white/5"
                      onError={() => setEditPreviewFailed(true)}
                    />
                  )}
                  <span className="text-xs text-ink/50 dark:text-frost-muted">
                    Pré-visualização{editPreviewFailed ? ' · URL não carrega' : ''}
                  </span>
                </div>
              ) : editPhotoFile ? (
                <p className="text-xs text-ink/50 dark:text-frost-muted">
                  Nova imagem será enviada ao guardar: {editPhotoFile.name}
                </p>
              ) : null}
              <div className="space-y-3 rounded-2xl border border-ink/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
                  Mais fotos e YouTube
                </p>
                {[2, 3, 4].map((slot, idx) => (
                  <div key={slot} className="grid gap-2 sm:grid-cols-2 sm:items-end">
                    <label className="text-sm font-semibold">
                      URL foto {slot}
                      <input
                        type="text"
                        inputMode="url"
                        disabled={!!editPhotoExtra[idx]}
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-[#252f2a]"
                        value={slot === 2 ? editing.imagem_2 : slot === 3 ? editing.imagem_3 : editing.imagem_4}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            ...(slot === 2
                              ? { imagem_2: e.target.value }
                              : slot === 3
                                ? { imagem_3: e.target.value }
                                : { imagem_4: e.target.value }),
                          })
                        }
                      />
                    </label>
                    <label className="text-sm font-semibold">
                      Novo ficheiro foto {slot}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                        className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
                        onChange={(e) => {
                          const f = e.target.files?.[0] ?? null;
                          setEditPhotoExtra((prev) => {
                            const n = [...prev];
                            n[idx] = f;
                            return n;
                          });
                        }}
                      />
                    </label>
                  </div>
                ))}
                {editPhotoExtra.some(Boolean) ? (
                  <p className="text-xs text-ink/50 dark:text-frost-muted">
                    Novas imagens nos slots marcados serão enviadas ao guardar.
                  </p>
                ) : null}
                <label className="block text-sm font-semibold">
                  Link YouTube (vazio remove o vídeo na loja)
                  <input
                    type="text"
                    inputMode="url"
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editing.youtube_video_url}
                    onChange={(e) => setEditing({ ...editing, youtube_video_url: e.target.value })}
                  />
                </label>
              </div>
              <label className="text-sm font-semibold">
                Seção
                <input
                  list="produto-secao-sugestoes"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.secao}
                  onChange={(e) => setEditing({ ...editing, secao: e.target.value })}
                />
              </label>
              <label className="text-sm font-semibold">
                Grupo
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editing.grupo}
                  onChange={(e) => setEditing({ ...editing, grupo: e.target.value })}
                />
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={Boolean(editing.destaque)}
                  onChange={(e) => setEditing({ ...editing, destaque: e.target.checked })}
                  className="h-4 w-4 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
                />
                Em destaque na vitrine
              </label>
              <label className="text-sm font-semibold">
                Descrição
                <textarea
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  rows={3}
                  value={editing.descricao}
                  onChange={(e) => setEditing({ ...editing, descricao: e.target.value })}
                />
              </label>
              {error && <p className="text-sm font-semibold text-accent2">{error}</p>}
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="submit" className="rounded-2xl bg-accent px-5 py-2.5 text-sm font-semibold text-white">
                  Guardar alterações
                </button>
                <button
                  type="button"
                  className="rounded-2xl border border-ink/15 px-5 py-2.5 text-sm font-semibold dark:border-white/20"
                  onClick={() => {
                    setEditing(null);
                    setError('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#161f1c]">
            <p className="font-semibold text-ink dark:text-white">Excluir produto?</p>
            <p className="mt-2 text-sm text-ink/70 dark:text-mist/70">
              Esta ação não pode ser desfeita: <strong className="text-ink dark:text-white">{deleteTarget.nome}</strong>
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-2xl bg-accent2 py-2.5 text-sm font-semibold text-white"
                onClick={confirmDelete}
              >
                Excluir
              </button>
              <button
                type="button"
                className="flex-1 rounded-2xl border border-ink/15 py-2.5 text-sm font-semibold dark:border-white/20"
                onClick={() => setDeleteTarget(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <datalist id="produto-secao-sugestoes">
        {sectionNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </div>
  );
}
